import React, { FC } from 'react';
import {
  Asset,
  CalendarIcon,
  DatePicker,
  Modal,
  ModalContent,
} from '@clearcover/atlas';
import { FormInput } from 'Shared/components';
import { LabelProps } from 'Shared/components/FormInput/Variants';
import styles from './styles.module.css';
import { formatDateShort } from 'Shared/utils';
import classNames from 'classnames';

type DatePickerModalProps = {
  className?: string;
  disabled?: boolean;
  errorMessage?: string;
  label?: string;
  name?: string;
  maxDateAllowed?: Date;
  minDateAllowed?: Date;
  modalHeader?: string;
  onDateChange: (date: Date) => void;
  startDate?: Date;
  loading?: boolean;
  tooltip?: LabelProps['tooltip'];
  tooltipPosition?: LabelProps['tooltipPosition'];
  placeholder?: string;
  isClearable?: boolean;
  onClear?: () => void;
  formInputClassName?: string;
};

export const formatDatePickerDate = (date: Date) =>
  formatDateShort(date.toISOString());

export const DatePickerModal: FC<DatePickerModalProps> = ({
  className,
  disabled = false,
  errorMessage,
  label,
  name,
  maxDateAllowed,
  minDateAllowed,
  modalHeader,
  onDateChange,
  startDate,
  loading,
  tooltip,
  tooltipPosition,
  placeholder,
  isClearable,
  onClear,
  formInputClassName,
}) => {
  return (
    <Modal
      variant="normal"
      data-testid="date-picker-modal"
      toggle={open => (
        <div className={className}>
          <FormInput
            readOnly
            disabled={disabled}
            className={formInputClassName}
            variant="text"
            icon={
              <Asset
                className={classNames(styles.icon, {
                  [styles.disabled]: disabled,
                })}
                icon={CalendarIcon}
                description="Calendar Icon"
                height={20}
                width={20}
              />
            }
            name={name ?? 'date'}
            maxLength={10}
            onClick={open}
            value={startDate ? startDate.toLocaleDateString() : ''}
            label={label}
            errorMessage={errorMessage}
            loading={loading}
            tooltip={tooltip}
            tooltipPosition={tooltipPosition}
            placeholder={placeholder}
            isClearable={isClearable}
            onClear={onClear}
          />
        </div>
      )}
    >
      {close => (
        <>
          {modalHeader && (
            <div
              data-testid="date-picker-header"
              className={styles.datePickerHeader}
            >
              {modalHeader}
            </div>
          )}
          <ModalContent className={styles.datePickerContainer}>
            <DatePicker
              data-testid="date-picker"
              className={styles.datePicker}
              initializeDate={startDate ?? new Date()}
              minDateAllowed={minDateAllowed}
              maxDateAllowed={maxDateAllowed}
              onSelect={date => {
                onDateChange(date);
                close();
              }}
            />
          </ModalContent>
        </>
      )}
    </Modal>
  );
};
